import { useCallback, useRef, useState } from 'react';
import { SLOT_MINUTES } from '@boud/contracts';
import { yToMinutes } from '../../lib/calendar';

export interface DragSelection {
  /** The column being dragged in — a date in the week view, a room in the day view. */
  key: string;
  fromMinute: number;
  toMinute: number;
}

/**
 * Drag across a column to pick a time (REQ-037).
 *
 * Pointer events, not the prototype's three parallel mouse/touch handler sets.
 * One set of events covers mouse, touch and stylus, and `setPointerCapture`
 * means the drag keeps tracking when the pointer leaves the column — the
 * prototype's `mousemove` on the column itself froze the ghost the moment you
 * dragged past its edge, which is exactly when a user is trying to extend the
 * selection.
 *
 * The listener is removed by React's own effect teardown rather than the
 * prototype's manual array of cleanup functions, which had to be remembered at
 * every call site and was the reason its calendar leaked a `mouseup` handler on
 * every re-render.
 */
export function useDragToCreate(onComplete: (selection: DragSelection) => void): {
  selection: DragSelection | null;
  handlers: (key: string) => {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  };
} {
  const [selection, setSelection] = useState<DragSelection | null>(null);
  const anchorRef = useRef<{ key: string; minute: number } | null>(null);

  const minuteAt = (event: React.PointerEvent<HTMLElement>): number => {
    const rect = event.currentTarget.getBoundingClientRect();
    return yToMinutes(event.clientY - rect.top);
  };

  const handlers = useCallback(
    (key: string) => ({
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
        // A click that lands on an existing booking opens it; it does not start
        // a drag on top of it.
        if ((event.target as HTMLElement).closest('.cal-event')) return;
        if (event.button !== 0 && event.pointerType === 'mouse') return;

        const minute = minuteAt(event);
        anchorRef.current = { key, minute };
        setSelection({ key, fromMinute: minute, toMinute: minute + SLOT_MINUTES });

        event.currentTarget.setPointerCapture(event.pointerId);
        // Stops the browser turning the drag into a text selection or a scroll.
        event.preventDefault();
      },

      onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
        const anchor = anchorRef.current;
        if (!anchor || anchor.key !== key) return;

        setSelection({
          key,
          fromMinute: anchor.minute,
          // Never shorter than one slot, so a click without movement still means
          // something rather than producing a zero-length booking.
          toMinute: Math.max(minuteAt(event), anchor.minute + SLOT_MINUTES),
        });
      },

      onPointerUp: (event: React.PointerEvent<HTMLElement>) => {
        const anchor = anchorRef.current;
        if (!anchor || anchor.key !== key) return;

        const toMinute = Math.max(minuteAt(event), anchor.minute + SLOT_MINUTES);
        anchorRef.current = null;
        setSelection(null);

        onComplete({ key, fromMinute: anchor.minute, toMinute });
      },

      onPointerCancel: () => {
        anchorRef.current = null;
        setSelection(null);
      },
    }),
    [onComplete],
  );

  return { selection, handlers };
}
